import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayName } from '../lib/displayName';
import { useConfirm } from '../hooks/useConfirm';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Pill, Plus, Search, CheckCircle2, Circle,
  Trash2, Edit, X, Syringe, Layers, MoreVertical, Save, History
} from 'lucide-react';
import apiClient from '../api/client';
import SupplementPlanHistoryDialog from './SupplementPlanHistoryDialog';
import RecentlyDeletedSupplementPlans from './RecentlyDeletedSupplementPlans';
import { SupplementDayPicker, OrderedTimesPicker, DEFAULT_DAYS_OF_WEEK, getFrequencyLabel } from './SupplementDayPicker';
import {
  planStatusLabel, DURATION_UNITS, maxForUnit, splitDurationDays, durationDaysFrom, previewEndDate, formatEndDate,
} from '../lib/supplementPlanStatus';

export default function SupplementPlanSheet({ client, open, onClose, selfManaged = false }) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // Create/Edit plan state
  const [showPlanEditor, setShowPlanEditor] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planName, setPlanName] = useState('');
  const [planSupplements, setPlanSupplements] = useState([]);
  const [planNotes, setPlanNotes] = useState('');
  const [planDurationValue, setPlanDurationValue] = useState(''); // '' = no expiry
  const [planDurationUnit, setPlanDurationUnit] = useState('days'); // 'days' | 'weeks' | 'months'

  // Add supplement picker
  const [showPicker, setShowPicker] = useState(false);
  const [suppSearch, setSuppSearch] = useState('');
  const [suppCategory, setSuppCategory] = useState('');
  const [suppResults, setSuppResults] = useState([]);

  // Add custom supplement
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customDosage, setCustomDosage] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [savingCustom, setSavingCustom] = useState(false);

  // Delete confirmation
  const [deletePlanConfirm, setDeletePlanConfirm] = useState(null);

  // Version history — the plan whose history is open, or null
  const [historyPlan, setHistoryPlan] = useState(null);

  // Import modal
  const [showImport, setShowImport] = useState(false);
  const [importTab, setImportTab] = useState('templates'); // 'templates' | 'client'
  const [importTemplates, setImportTemplates] = useState([]);
  const [importClients, setImportClients] = useState([]);
  const [importSelectedClient, setImportSelectedClient] = useState('');
  const [importClientPlans, setImportClientPlans] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  // Save as stack template
  const [saveAsStackPlan, setSaveAsStackPlan] = useState(null);
  const [stackTemplateName, setStackTemplateName] = useState('');
  const [savingStack, setSavingStack] = useState(false);

  const openSaveAsStack = (plan) => {
    setStackTemplateName(plan.name);
    setSaveAsStackPlan(plan);
  };

  const saveAsStack = async () => {
    if (!stackTemplateName.trim() || !saveAsStackPlan) return;
    setSavingStack(true);
    try {
      await apiClient.post('/supplement-stacks', {
        name: stackTemplateName,
        supplements: saveAsStackPlan.supplements || [],
        notes: saveAsStackPlan.notes || '',
      });
      toast.success(t('supplementPlan.toastSavedAsStack'));
      setSaveAsStackPlan(null);
    } catch (err) {
      toast.error(t('supplementPlan.toastFailedSaveTemplate'));
    } finally {
      setSavingStack(false);
    }
  };

  const fetchPlans = useCallback(async () => {
    if (!client?.id) return;
    try {
      const [plansRes, catsRes] = await Promise.all([
        apiClient.get(`/supplement-plans?client_id=${client.id}`),
        apiClient.get('/supplements/categories'),
      ]);
      // In self-managed mode only show plans the user owns themselves — coach-assigned plans
      // are read-only from the daily tracking view, not editable from this sheet.
      const filtered = selfManaged
        ? (plansRes.data || []).filter(p => p.coach_id === client.id)
        : (plansRes.data || []);
      setPlans(filtered);
      setCategories(catsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [client?.id, selfManaged]);

  useEffect(() => {
    if (open && client?.id) {
      setLoading(true);
      fetchPlans();
    }
  }, [open, client?.id, fetchPlans]);

  // Supplement search
  const searchSupplements = async (search = '', category = '') => {
    try {
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      const res = await apiClient.get('/supplements', { params });
      setSuppResults(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (showPicker) searchSupplements(suppSearch, suppCategory);
  }, [showPicker, suppSearch, suppCategory]);

  const sortedCategories = useMemo(() =>
    [...categories].sort((a, b) => {
      if (a === 'Anabolic Steroids') return 1;
      if (b === 'Anabolic Steroids') return -1;
      return a.localeCompare(b);
    }), [categories]);

  const openImport = async () => {
    setImportLoading(true);
    setImportTemplates([]);
    setImportClients([]);
    setImportSelectedClient('');
    setImportClientPlans([]);
    setImportTab('templates');
    setShowImport(true);
    try {
      const [templatesRes, clientsRes] = await Promise.all([
        apiClient.get('/supplement-stacks'),
        apiClient.get('/users/my-clients'),
      ]);
      setImportTemplates(templatesRes.data || []);
      setImportClients(clientsRes.data || []);
    } catch {
      toast.error(t('supplementPlan.toastFailedLoadImport'));
    } finally {
      setImportLoading(false);
    }
  };

  const loadClientPlans = async (clientId) => {
    setImportSelectedClient(clientId);
    if (!clientId) { setImportClientPlans([]); return; }
    try {
      const res = await apiClient.get(`/supplement-plans?client_id=${clientId}`);
      setImportClientPlans((res.data || []).filter(p => p.active));
    } catch {
      toast.error(t('supplementPlan.toastFailedLoadClientPlans'));
      setImportClientPlans([]);
    }
  };

  const applyImport = async (source) => {
    if (planSupplements.length > 0) {
      const ok = await confirm({
        title: t('supplementPlan.confirmReplaceSupplements'),
        confirmLabel: t('common.confirm'),
        cancelLabel: t('common.cancel'),
        destructive: true,
      });
      if (!ok) return;
    }
    setPlanName(source.name);
    setPlanSupplements((source.supplements || []).map(s => ({ ...s, times: [...(s.times || [])] })));
    setShowImport(false);
    toast.success(t('supplementPlan.toastImported', { name: source.name }));
  };

  const resetForm = () => {
    setPlanName('');
    setPlanSupplements([]);
    setPlanNotes('');
    setPlanDurationValue('');
    setPlanDurationUnit('days');
    setEditingPlan(null);
  };

  const openCreate = () => {
    resetForm();
    setShowPlanEditor(true);
  };

  const openEdit = (plan) => {
    setPlanName(plan.name);
    setPlanSupplements(plan.supplements || []);
    setPlanNotes(plan.notes || '');
    const dp = splitDurationDays(plan.duration_days);
    setPlanDurationValue(dp.value);
    setPlanDurationUnit(dp.unit);
    setEditingPlan(plan);
    setShowPlanEditor(true);
  };

  const addSupplement = (supp) => {
    if (planSupplements.find(s => s.supplement_id === supp.id)) {
      toast.error(t('supplementPlan.toastAlreadyAdded'));
      return;
    }
    setPlanSupplements(prev => [...prev, {
      supplement_id: supp.id,
      supplement_name: supp.name,
      dosage: supp.common_dosage || '',
      days_of_week: { ...DEFAULT_DAYS_OF_WEEK },
      times: ['morning'],
      notes: '',
    }]);
    setShowPicker(false);
    toast.success(t('supplementPlan.toastSupplementAdded', { name: supp.name }));
  };

  const openCustomDialog = () => {
    setCustomName(suppSearch.trim());  // pre-fill with whatever they were searching
    setCustomDosage('');
    setCustomCategory(suppCategory || categories[0] || 'Other');
    setShowCustomDialog(true);
  };

  const saveCustomSupplement = async () => {
    const name = customName.trim();
    if (!name) {
      toast.error(t('supplementPlan.errorCustomName'));
      return;
    }
    setSavingCustom(true);
    try {
      const res = await apiClient.post('/supplements/custom', {
        name,
        category: customCategory || 'Other',
        common_dosage: customDosage || '',
        description: '',
        benefits: '',
      });
      // Insert into the picker results so the user can see what they just added, and auto-add to the plan
      setSuppResults(prev => [res.data, ...prev]);
      addSupplement(res.data);
      setShowCustomDialog(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('supplementPlan.toastFailedCreateCustom'));
    } finally {
      setSavingCustom(false);
    }
  };

  const removeSupplement = (idx) => {
    setPlanSupplements(prev => prev.filter((_, i) => i !== idx));
  };

  const updateSupplement = (idx, field, value) => {
    setPlanSupplements(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const updateDays = (idx, newDays) => {
    setPlanSupplements(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const maxDoses = Math.max(...Object.values(newDays).map(v => Number(v) || 0), 0);
      const trimmed = (s.times || []).slice(0, Math.max(maxDoses, 1));
      return { ...s, days_of_week: newDays, times: trimmed };
    }));
  };

  const updateTimes = (idx, newTimes) => {
    setPlanSupplements(prev => prev.map((s, i) => i !== idx ? s : { ...s, times: newTimes }));
  };

  const savePlan = async () => {
    if (!planName.trim() || planSupplements.length === 0) {
      toast.error(t('supplementPlan.errorNameAndSupplement'));
      return;
    }
    const durationDays = durationDaysFrom(planDurationValue, planDurationUnit); // 0 = no expiry
    try {
      if (editingPlan) {
        await apiClient.put(`/supplement-plans/${editingPlan.id}`, {
          name: planName, supplements: planSupplements, notes: planNotes,
          duration_days: durationDays, // 0 clears it
        });
        toast.success(t('supplementPlan.toastPlanUpdated'));
      } else {
        await apiClient.post('/supplement-plans', {
          name: planName, client_id: client.id, supplements: planSupplements, notes: planNotes,
          duration_days: durationDays >= 1 ? durationDays : null,
        });
        toast.success(t('supplementPlan.toastPlanCreated'));
      }
      setShowPlanEditor(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      toast.error(t('supplementPlan.toastFailedSavePlan'));
    }
  };

  const deletePlan = async () => {
    if (!deletePlanConfirm) return;
    try {
      await apiClient.delete(`/supplement-plans/${deletePlanConfirm.id}`);
      toast.success(t('supplementPlan.toastPlanDeleted'));
      setDeletePlanConfirm(null);
      fetchPlans();
    } catch (err) {
      toast.error(t('supplementPlan.toastFailedDelete'));
    }
  };

  const toggleActive = async (plan) => {
    try {
      await apiClient.put(`/supplement-plans/${plan.id}`, { active: !plan.active });
      toast.success(plan.active ? t('supplementPlan.toastPlanDeactivated') : t('supplementPlan.toastPlanActivated'));
      fetchPlans();
    } catch (err) {
      toast.error(t('supplementPlan.toastFailedUpdate'));
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" side="right">
          <SheetHeader className="pb-4">
            <SheetTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {selfManaged
                ? t('supplementPlan.sheetTitleSelf')
                : <>{t('supplementPlan.sheetTitle')} — <span className="text-primary">{getDisplayName(client?.name).toUpperCase()}</span></>}
            </SheetTitle>
          </SheetHeader>

          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : (
            <div className="space-y-4">
              <Button onClick={openCreate} className="w-full bg-primary hover:bg-primary/90" data-testid="create-supplement-plan-btn">
                <Plus className="h-4 w-4 mr-1.5" /> {t('supplementPlan.newPlan')}
              </Button>

              {plans.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Pill className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{t('supplementPlan.noPlansAssigned')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {plans.map(plan => (
                    <Card key={plan.id} className={`border-border/50 ${!plan.active ? 'opacity-50' : ''}`} data-testid={`plan-card-${plan.id}`}>
                      <CardContent className="py-3 px-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-sm">{plan.name}</h4>
                              <Badge variant={plan.active ? 'default' : 'secondary'} className={`text-xs ${plan.active ? 'bg-green-500/10 text-green-600 border-green-500/30' : ''}`}>
                                {plan.active ? t('supplementPlan.active') : t('supplementPlan.inactive')}
                              </Badge>
                              {planStatusLabel(plan, t) && (
                                <Badge variant="outline" className="text-xs">{planStatusLabel(plan, t)}</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{t('supplementPlan.supplementsCount', { count: plan.supplements?.length || 0 })}</p>
                          </div>
                          <div className="flex gap-1 items-center">
                            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" tooltip="Toggle active" onClick={() => toggleActive(plan)}>
                              {plan.active ? <Circle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" aria-label={t('tooltips.openMenu')}>
                                  <MoreVertical className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.id}`}>
                                  <Edit className="h-4 w-4 mr-2" /> {t('supplementPlan.editPlan')}
                                </DropdownMenuItem>
                                {!selfManaged && (
                                  <DropdownMenuItem onClick={() => openSaveAsStack(plan)}>
                                    <Save className="h-4 w-4 mr-2" /> {t('supplementPlan.saveAsStackTemplate')}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => setHistoryPlan(plan)} data-testid={`history-plan-${plan.id}`}>
                                  <History className="h-4 w-4 mr-2" /> {t('supplementPlan.viewHistory', 'Version history')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setDeletePlanConfirm(plan)} className="text-destructive focus:text-destructive" data-testid={`delete-plan-${plan.id}`}>
                                  <Trash2 className="h-4 w-4 mr-2" /> {t('supplementPlan.deletePlan')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(plan.supplements || []).map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-normal">
                              {s.supplement_name} — {s.dosage} · {getFrequencyLabel(s.days_of_week || {})}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Recently deleted — hides itself when the Trash is empty */}
              <RecentlyDeletedSupplementPlans onRestored={fetchPlans} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Plan Editor Dialog */}
      <Dialog open={showPlanEditor} onOpenChange={(o) => { if (!o) { setShowPlanEditor(false); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {editingPlan ? t('supplementPlan.editLabel') : t('supplementPlan.createLabel')} <span className="text-primary">{t('supplementPlan.planEditorTitle')}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Import button — coach feature only; self-managed users build their plan from scratch */}
            {!editingPlan && !selfManaged && (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs gap-1.5"
                onClick={openImport}
              >
                <Layers className="h-3.5 w-3.5" /> {t('supplementPlan.importFromTemplateOrClient')}
              </Button>
            )}

            <div>
              <Label>{t('supplementPlan.planName')}</Label>
              <Input value={planName} onChange={e => setPlanName(e.target.value)} placeholder={t('supplementPlan.planNamePlaceholder')} data-testid="plan-name-input" />
            </div>

            <div>
              <Label>{t('supplementPlan.notesOptional')}</Label>
              <Input value={planNotes} onChange={e => setPlanNotes(e.target.value)} placeholder={t('supplementPlan.notesPlaceholder')} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="plan-duration">{t('supplementPlan.durationLabel')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="plan-duration"
                  type="number"
                  min="1"
                  max={maxForUnit(planDurationUnit)}
                  inputMode="numeric"
                  className="w-28"
                  placeholder={t('supplementPlan.durationPlaceholder')}
                  value={planDurationValue}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setPlanDurationValue(v === '' ? '' : String(Math.min(maxForUnit(planDurationUnit), parseInt(v, 10))));
                  }}
                  aria-describedby="plan-duration-help"
                  data-testid="plan-duration-input"
                />
                <select
                  aria-label={t('supplementPlan.durationLabel')}
                  className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                  value={planDurationUnit}
                  onChange={(e) => {
                    const u = e.target.value;
                    setPlanDurationUnit(u);
                    setPlanDurationValue((prev) => (prev === '' ? '' : String(Math.min(maxForUnit(u), parseInt(prev, 10)))));
                  }}
                  data-testid="plan-duration-unit"
                >
                  {DURATION_UNITS.map((u) => (
                    <option key={u.key} value={u.key}>
                      {t(`supplementPlan.durationUnit${u.key.charAt(0).toUpperCase()}${u.key.slice(1)}`)}
                    </option>
                  ))}
                </select>
              </div>
              {(() => {
                const end = previewEndDate(editingPlan, durationDaysFrom(planDurationValue, planDurationUnit));
                return end ? (
                  <p className="text-xs text-primary">{t('supplementPlan.endsOn', { date: formatEndDate(end, i18n.language) })}</p>
                ) : null;
              })()}
              <p id="plan-duration-help" className="text-xs text-muted-foreground">{t('supplementPlan.durationHelp')}</p>
            </div>

            {/* Supplements list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t('supplementPlan.supplementsLabel', { count: planSupplements.length })}</Label>
                <Button variant="outline" size="sm" onClick={() => { setShowPicker(true); setSuppSearch(''); setSuppCategory(''); }} data-testid="add-supplement-btn">
                  <Plus className="h-3 w-3 mr-1" /> {t('common.add')}
                </Button>
              </div>

              {planSupplements.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground border border-dashed rounded-lg">
                  {t('supplementPlan.noSupplementsAdded')}
                </div>
              ) : (
                <div className="space-y-3">
                  {planSupplements.map((supp, idx) => (
                    <Card key={idx} className="border-border/50">
                      <CardContent className="py-3 px-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">{supp.supplement_name}</p>
                          <Button variant="ghost" size="icon" className="h-9 w-9 md:h-6 md:w-6 text-destructive" tooltip="Remove" onClick={() => removeSupplement(idx)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>

                        <div>
                          <Label className="text-xs">{t('supplementPlan.dosage')}</Label>
                          <Input value={supp.dosage} onChange={e => updateSupplement(idx, 'dosage', e.target.value)} className="h-11 md:h-8 text-base md:text-xs" />
                        </div>

                        <div>
                          <Label className="text-xs mb-1 block">{t('supplementPlan.schedule')}</Label>
                          <SupplementDayPicker
                            value={supp.days_of_week || DEFAULT_DAYS_OF_WEEK}
                            onChange={newDays => updateDays(idx, newDays)}
                          />
                        </div>

                        <OrderedTimesPicker
                          days={supp.days_of_week || DEFAULT_DAYS_OF_WEEK}
                          times={supp.times || []}
                          onChange={newTimes => updateTimes(idx, newTimes)}
                        />

                        <div>
                          <Label className="text-xs">{t('supplementPlan.notes')}</Label>
                          <Input value={supp.notes || ''} onChange={e => updateSupplement(idx, 'notes', e.target.value)} className="h-11 md:h-8 text-base md:text-xs" placeholder={t('supplementPlan.suppNotesPlaceholder')} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowPlanEditor(false); resetForm(); }}>{t('common.cancel')}</Button>
            <Button onClick={savePlan} className="bg-primary hover:bg-primary/90" disabled={!planName.trim() || planSupplements.length === 0} data-testid="save-plan-btn">
              {editingPlan ? t('supplementPlan.updatePlan') : t('supplementPlan.createPlan')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplement Picker Dialog */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-md flex flex-col" style={{ maxHeight: '85dvh' }}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('supplementPlan.addSupplement')}</DialogTitle>
          </DialogHeader>
          <>
            <div className="relative flex-shrink-0 mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={suppSearch} onChange={e => setSuppSearch(e.target.value)} placeholder={t('supplementPlan.searchSupplements')} className="pl-10" data-testid="supplement-search-input" />
            </div>
            <div className="flex gap-1 flex-wrap mb-2 flex-shrink-0">
              <button onClick={() => setSuppCategory('')} className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${!suppCategory ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>{t('supplementPlan.allCategories')}</button>
              {sortedCategories.map(cat => (
                <button key={cat} onClick={() => setSuppCategory(cat)}
                  className={`px-2 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${suppCategory === cat ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  {cat === 'Anabolic Steroids' ? <Syringe className="h-3.5 w-3.5" /> : cat}
                </button>
              ))}
            </div>
            <div className="overflow-y-auto flex-1 min-h-0">
              <div className="space-y-1 pr-1">
                {suppResults.map(supp => (
                  <div key={supp.id} onClick={() => addSupplement(supp)}
                    className="flex items-center justify-between p-2.5 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors" data-testid={`pick-supplement-${supp.id}`}>
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2">
                        <span>{supp.name}</span>
                        {supp.is_custom && <Badge variant="outline" className="text-[10px] py-0">{t('supplementPlan.customBadge')}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{supp.category} — {supp.common_dosage}</p>
                    </div>
                    <Plus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </div>
                ))}
                {suppResults.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">{t('supplementPlan.noSupplementsFound')}</p>}
              </div>
            </div>
            <div className="flex-shrink-0 pt-2 border-t border-border/50 mt-2">
              <Button variant="outline" size="sm" className="w-full text-xs gap-1.5" onClick={openCustomDialog} data-testid="add-custom-supplement-btn">
                <Plus className="h-3.5 w-3.5" /> {t('supplementPlan.addCustom')}
              </Button>
            </div>
          </>
        </DialogContent>
      </Dialog>

      {/* Add Custom Supplement Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={(o) => { if (!o) setShowCustomDialog(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('supplementPlan.addCustomTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="custom-supp-name">{t('supplementPlan.customNameLabel')}</Label>
              <Input id="custom-supp-name" value={customName} onChange={e => setCustomName(e.target.value)} placeholder={t('supplementPlan.customNamePlaceholder')} data-testid="custom-supp-name" />
            </div>
            <div>
              <Label htmlFor="custom-supp-dosage">{t('supplementPlan.customDosageLabel')}</Label>
              <Input id="custom-supp-dosage" value={customDosage} onChange={e => setCustomDosage(e.target.value)} placeholder={t('supplementPlan.customDosagePlaceholder')} />
            </div>
            <div>
              <Label htmlFor="custom-supp-category">{t('supplementPlan.customCategoryLabel')}</Label>
              <select id="custom-supp-category" value={customCategory} onChange={e => setCustomCategory(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                {sortedCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                {!sortedCategories.includes('Other') && <option value="Other">Other</option>}
              </select>
            </div>
            <p className="text-xs text-muted-foreground">{t('supplementPlan.customPrivateNote')}</p>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setShowCustomDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={saveCustomSupplement} className="bg-primary hover:bg-primary/90" disabled={!customName.trim() || savingCustom} data-testid="save-custom-supp-btn">
              {savingCustom ? t('supplementPlan.saving') : t('supplementPlan.addSupplement')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletePlanConfirm} onOpenChange={(o) => { if (!o) setDeletePlanConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('supplementPlan.deletePlanTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('supplementPlan.deletePlanConfirmPrefix')}<strong>{deletePlanConfirm?.name}</strong>{t('supplementPlan.deletePlanConfirmSuffix')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={deletePlan} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-plan">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save as Stack Template Dialog */}
      <Dialog open={!!saveAsStackPlan} onOpenChange={(o) => { if (!o) setSaveAsStackPlan(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('supplementPlan.saveAsStack')} <span className="text-primary">{t('supplementPlan.stackTemplate')}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('supplementPlan.templateName')}</Label>
              <Input
                value={stackTemplateName}
                onChange={e => setStackTemplateName(e.target.value)}
                placeholder={t('supplementPlan.stackNamePlaceholder')}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('supplementPlan.supplementsWillBeSaved', { count: saveAsStackPlan?.supplements?.length || 0 })}
            </p>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setSaveAsStackPlan(null)}>{t('common.cancel')}</Button>
            <Button
              onClick={saveAsStack}
              className="bg-primary hover:bg-primary/90"
              disabled={!stackTemplateName.trim() || savingStack}
            >
              {savingStack ? t('supplementPlan.saving') : t('supplementPlan.saveTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImport} onOpenChange={(o) => { if (!o) setShowImport(false); }}>
        <DialogContent className="max-w-md flex flex-col" style={{ maxHeight: '85dvh' }}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('supplementPlan.importTitle')} <span className="text-primary">{t('supplementPlan.importSubtitle')}</span>
            </DialogTitle>
            <div className="flex gap-1 mt-2">
              <button
                onClick={() => setImportTab('templates')}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${importTab === 'templates' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                {t('supplementPlan.fromTemplates')}
              </button>
              <button
                onClick={() => setImportTab('client')}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${importTab === 'client' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                {t('supplementPlan.fromClient')}
              </button>
            </div>
          </DialogHeader>

          {importLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : importTab === 'templates' ? (
            <div className="overflow-y-auto flex-1 min-h-0 mt-2">
              {importTemplates.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Pill className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('supplementPlan.noTemplatesYet')}</p>
                  <p className="text-xs mt-1">{t('supplementPlan.createTemplatesHint')}</p>
                </div>
              ) : (
                <div className="space-y-2 pr-1">
                  {importTemplates.map(tpl => (
                    <div
                      key={tpl.id}
                      className="border border-border/50 rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => applyImport(tpl)}
                    >
                      <p className="font-medium text-sm">{tpl.name}</p>
                      <p className="text-xs text-muted-foreground">{t('supplementPlan.supplementsCount', { count: tpl.supplements?.length || 0 })}</p>
                      {tpl.notes && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{tpl.notes}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(tpl.supplements || []).slice(0, 4).map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs font-normal">
                            {s.supplement_name}
                          </Badge>
                        ))}
                        {(tpl.supplements || []).length > 4 && (
                          <Badge variant="outline" className="text-xs font-normal">
                            +{tpl.supplements.length - 4} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2 space-y-3 overflow-y-auto flex-1 min-h-0">
              <select
                value={importSelectedClient}
                onChange={e => loadClientPlans(e.target.value)}
                className="w-full text-sm border border-border rounded-md px-3 py-2 bg-background"
              >
                <option value="">{t('supplementPlan.selectClient')}</option>
                {importClients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              {importSelectedClient && importClientPlans.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">
                  {t('supplementPlan.noActivePlansForClient')}
                </p>
              )}

              {importClientPlans.length > 0 && (
                <div className="space-y-2 pr-1">
                  {importClientPlans.map(plan => (
                    <div
                      key={plan.id}
                      className="border border-border/50 rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => applyImport(plan)}
                    >
                      <p className="font-medium text-sm">{plan.name}</p>
                      <p className="text-xs text-muted-foreground">{t('supplementPlan.supplementsCount', { count: plan.supplements?.length || 0 })}</p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(plan.supplements || []).slice(0, 4).map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs font-normal">
                            {s.supplement_name}
                          </Badge>
                        ))}
                        {(plan.supplements || []).length > 4 && (
                          <Badge variant="outline" className="text-xs font-normal">
                            +{plan.supplements.length - 4} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SupplementPlanHistoryDialog
        open={!!historyPlan}
        planId={historyPlan?.id || null}
        currentPlan={historyPlan}
        onClose={() => setHistoryPlan(null)}
        onRestored={fetchPlans}
      />
    </>
  );
}
